import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiHandler from "./../../../services/api/ApiHandler";
import apiPayload from './../../../services/api/ApiPayload';
import localStorageService from './../../../helpers/store/StorageService';
import CloudLoader from './../loader/Loader_02';
import VirtualKeyboard from '../virtual/Keyboard';

const ConnectUs = ({ contentData, initError, initLoader }) => {
  let { data, loading, error, handlePostApi } = ApiHandler();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.matchMedia('(max-width: 1023px)').matches);
  const [activeInput, setActiveInput] = useState(null);
  const inputRefs = useRef({});  // Refs for position
  const [step, setStep] = useState(1);  // Current step (1 to 8)
  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
    loginCode: '',
  });
  const [errors, setErrors] = useState({});  // Field-specific errors
  const [isLoading, setIsLoading] = useState(false);  // For API loading
  const [serverError, setServerError] = useState(null);  // For API uniqueness errors

  // Validation functions (adapted from sign up, with min 2 symbols check)
  const validateIdentifier = (identifier) => identifier.trim().length >= 1;
  const validatePassword = (password) => password.length >= 5;
  const validateLoginCode = (loginCode) => /^[0-9]{8}$/.test(loginCode.trim());

  // Validate current step's fields (min 2 symbols for enable)
  const validateCurrentStep = () => {
    let newErrors = {};
    switch (step) {
      case 1:
        if (!validateIdentifier(formData.identifier)) newErrors.identifier = 'Enter valid credentials';
        break;
      case 2:
        if (!validatePassword(formData.password)) newErrors.password = 'Enter valid credentials';
        break;
      case 3:
        if (!validateLoginCode(formData.loginCode)) newErrors.loginCode = 'Enter valid credentials';
        break;
      default:
        break;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate all fields before API submit
  const validateAll = () => {
    let newErrors = {};
    if (!validateIdentifier(formData.identifier)) newErrors.identifier = 'Enter valid credentials';
    if (!validatePassword(formData.password)) newErrors.password = 'Enter valid credentials';
    if (!validateLoginCode(formData.loginCode)) newErrors.loginCode = 'Enter valid credentials';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // Next button handler
  const handleNext = () => {
    if (validateCurrentStep()) {
      setStep((prev) => prev + 1);
      setErrors({});
      setServerError(null);
    }
  };

  // Previous button handler
  const handlePrevious = () => {
    setStep((prev) => prev - 1);
    setErrors({});
    setServerError(null);
  };

  // Submit handler
  const handleSubmit = async () => {
    if (validateAll()) {
      setIsLoading(true);
      setServerError(null);
      try {
        apiPayload.set('load.pageId', 'Page30005');
        apiPayload.set('load.redirectRoute', '/connect');
        apiPayload.set('load.data.userId', -1);
        apiPayload.set('load.data.identifier', formData.identifier);
        apiPayload.set('load.data.password', formData.password);
        apiPayload.set('load.data.loginCode', formData.loginCode);
        handlePostApi("/v1/connect", apiPayload.fetchPayload());
      } catch (err) {
        setServerError({ general: 'API error', error: error });
        console.warn(err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Render step content
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div>
            <label><b>Username</b> / <b>Email</b> / <b>Phone</b></label>
            <br/>
            <input ref={(el) => (inputRefs.current['identifier'] = el)} name="identifier" value={formData.identifier} onChange={handleChange} onBlur={(e) => { if (isMobile && !e.relatedTarget?.closest('.virtual-keyboard')) { handleCloseKeyboard(); } }} onClick={() => isMobile && setActiveInput('identifier')} onFocus={() => isMobile && setActiveInput('identifier')} readOnly={isMobile}/>
            {errors.identifier && <span className="error">{errors.identifier}</span>}
          </div>
        );
      case 2:
        return (
          <div>
            <label><b>Password</b></label>
            <br/>
            <input ref={(el) => (inputRefs.current['password'] = el)} type="password" name="password" value={formData.password} onChange={handleChange} onBlur={(e) => { if (isMobile && !e.relatedTarget?.closest('.virtual-keyboard')) { handleCloseKeyboard(); } }} onClick={() => isMobile && setActiveInput('password')} onFocus={() => isMobile && setActiveInput('password')} readOnly={isMobile}/>
            {errors.password && <span className="error">{errors.password}</span>}
          </div>
        );
      case 3:
        return (
          <div>
            <label><b>Access Code</b></label>
            <br/>
            <input ref={(el) => (inputRefs.current['loginCode'] = el)} name="loginCode" value={formData.loginCode} onChange={handleChange} onBlur={(e) => { if (isMobile && !e.relatedTarget?.closest('.virtual-keyboard')) { handleCloseKeyboard(); } }} onClick={() => isMobile && setActiveInput('loginCode')} onFocus={() => isMobile && setActiveInput('loginCode')} readOnly={isMobile}/>
            {errors.loginCode && <span className="error">{errors.loginCode}</span>}
          </div>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1023px)');
    const handleChange = () => setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Handle virtual key press
  const handleKeyPress = (key) => {
    if (activeInput) {
      let value = formData[activeInput];
      if (key === 'backspace') value = value.slice(0, -1);
      else value += key;
      setFormData((prev) => ({ ...prev, [activeInput]: value }));
    }
  };

  // Handle keyboard close
  const handleCloseKeyboard = () => setActiveInput(null);

  useEffect(() => {
    if (data && data.code === "200") {
      localStorageService.set('report', data.result.api_request_data.is_auth);
      localStorageService.set('active', data.result.api_request_data.user_id);
      window.location.href = '/getting-started';
    } else if (data && data.code !== "200") {
      setServerError({ general: data.result.api_request_data.error });
    }
  }, [formData, data, navigate]);

  return (
    <div className="join-page" style={{ position: 'relative' }}>
      {initLoader && <CloudLoader />}
      {isLoading && <CloudLoader />}
      {loading && <CloudLoader />}
      <h2>Connect with us</h2>
      <hr style={{border:'none', height:'2px', background:'linear-gradient(to right, #e600c7 0%, #940180 25%, rgb(0, 0, 0) 100%)', boxShadow: 'rgba(0, 0, 0, 0.5) 0px 1px 3px inset', margin:'20px 0px', width:'100%'}} />
      <p><b>{step}</b> / 3</p>
      <br/>
      {renderStep()}
      {activeInput && isMobile && (
        <VirtualKeyboard
          onKeyPress={handleKeyPress}
          onClose={handleCloseKeyboard}
          isVisible={isMobile && !!activeInput}
        />
      )}
      {serverError?.general && <span className="error">{serverError.general}</span>}
      <div className="buttons">
        {step > 1 && <button className="btn" onClick={handlePrevious}>Previous</button>}
        {step < 3 && <button className="btn" onClick={handleNext} disabled={isLoading}>Next</button>}
        {step === 3 && <button className="btn" onClick={handleSubmit} disabled={isLoading}>Connect Us</button>}
      </div>
      <hr style={{border:'none', height:'2px', background:'linear-gradient(to right, #e600c7 0%, #940180 25%, rgb(0, 0, 0) 100%)', boxShadow: 'rgba(0, 0, 0, 0.5) 0px 1px 3px inset', margin:'20px 0px', width:'100%'}} />
      <button className="nav-item" onClick={() => navigate("/join")}>New User? Create New Account</button>
    </div>
  );
};

export default ConnectUs;
